import React, { useEffect, useState } from 'react';
import InspectionItemTable, {
  manageItemStyle,
} from './components/InspectionItemTable';
import { Backdrop, Box, Button, CircularProgress } from '@mui/material';
import useErrorModal from '../../hooks/useErrorModal';
import ErrorModal from '../../_common/Modal/ErrorModal';
import { getCategoryMaster } from '../../services/api/categoryMaster';
import { getTypeMaster } from '../../services/api/typeMaster';
import { getItemMaster } from '../../services/api/itemMaster';
import { API_CODE } from '../../_constants/apiConst';
import {
  isGetCategoryMaster,
  isGetItemMaster,
  isGetTypeMaster,
} from '../../_utils/typeGuardUtils';
import {
  Attribute,
  CategoryMaster,
  ItemMaster,
  PFRCodeName,
  TypeMaster,
} from '../../services/apiTypes';
import {
  HEADER_HEIGHT,
  LOADING_BACKDROP_STYLE,
} from '../../_constants/styleConst';
import useExport from './hooks/useExport';
import {
  IMPORTABLE_EXCEL_FORMAT_LIST,
  MANAGE_ITEM_MODE,
} from '../../_constants/manageConst';
import useSortChangeMode from './hooks/useSortChangeMode';
import { FileUploadOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { RootState } from '../../stores/store';
import { isAdmin } from '../../_utils/utils';
import FileImportModal from '../../_common/Modal/FileImportModal';
import useImport from './hooks/useImport';

export type Options = {
  attribute1MasterList: Attribute[];
  attribute2MasterList: Attribute[];
  attribute3MasterList: Attribute[];
  attribute5MasterList: Attribute[];
  attribute6MasterList: Attribute[];
  brandMasterList: Attribute[];
  pCodeMasterList: PFRCodeName[];
  fCodeMasterList: PFRCodeName[];
  rCodeMasterList: PFRCodeName[];
};

const defaultOptions = {
  attribute1MasterList: [],
  attribute2MasterList: [],
  attribute3MasterList: [],
  attribute5MasterList: [],
  attribute6MasterList: [],
  brandMasterList: [],
  pCodeMasterList: [],
  fCodeMasterList: [],
  rCodeMasterList: [],
};

const ManageInspectionItem: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.userInfo);
  const [isLoading, setIsLoading] = useState(false);
  const [categoryMaster, setCategoryMaster] = useState<CategoryMaster[]>([]);
  const [typeMaster, setTypeMaster] = useState<TypeMaster[]>([]);
  const [itemMaster, setItemMaster] = useState<ItemMaster[]>([]);
  const [itemOptions, setItemOptions] = useState<Options>(defaultOptions);
  /** インポートのファイル */
  const [importFile, setImportFile] = useState<File | undefined>();
  /** インポートモーダル表示 */
  const [isOpenImportModal, setIsOpenImportModal] = useState(false);
  /** インポートのステータス */
  const [importStatus, setImportStatus] = useState<0 | 1 | 2 | 3 | 4>(0);
  /** インポートエラーメッセージ */
  const [importErrorMsg, setImportErrorMsg] = useState('');
  /** 診断項目管理の表示モード */
  const [displayMode, setDisplayMode] = useState(MANAGE_ITEM_MODE.VIEW);
  /** エラー表示 */
  const {
    error,
    hideError,
    showApiError,
    showTypeError,
    showClientError,
    getApiErrorMessage,
  } = useErrorModal();

  /** インポートのhook */
  const { onClickImportCancel, onClickImportOk } = useImport({
    itemOptions,
    importFile,
    setImportFile,
    setIsOpenImportModal,
    setImportErrorMsg,
    setImportStatus,
    getApiErrorMessage,
  });

  /** エクスポートのhook */
  const { doExportInspectionItemMaster } = useExport({
    setIsLoading,
    showApiError,
    showTypeError,
    showClientError,
  });

  /** 並び替えのhook */
  const { onClickSort, handleSortCancel, onClickSortSave } = useSortChangeMode({
    categoryMaster,
    setCategoryMaster,
    typeMaster,
    setTypeMaster,
    itemMaster,
    setItemMaster,
    setDisplayMode,
    setIsLoading,
    showApiError,
    showTypeError,
  });

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      try {
        await getCategoryMasterData();
        await getTypeMasterData();
        await getItemMasterData();
      } catch {
        // エラーハンドリングは各処理内で行う
      } finally {
        setIsLoading(false);
      }
    })();
  }, []);

  /** 大項目取得 */
  const getCategoryMasterData = async () => {
    try {
      const response = await getCategoryMaster();
      if (!isGetCategoryMaster(response)) {
        showTypeError(API_CODE.CATEGORY_MASTER);
        throw new Error('typeError');
      } else {
        setCategoryMaster(response);
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'typeError') {
        showApiError(error, API_CODE.CATEGORY_MASTER);
      }
      throw new Error();
    }
  };

  /** 中項目取得 */
  const getTypeMasterData = async () => {
    try {
      const response = await getTypeMaster();
      if (!isGetTypeMaster(response)) {
        showTypeError(API_CODE.TYPE_MASTER);
        throw new Error('typeError');
      } else {
        setTypeMaster(response);
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'typeError') {
        showApiError(error, API_CODE.TYPE_MASTER);
      }
      throw new Error();
    }
  };

  /** 小項目取得 */
  const getItemMasterData = async () => {
    try {
      const response = await getItemMaster();
      if (!isGetItemMaster(response)) {
        showTypeError(API_CODE.ITEM_MASTER);
        throw new Error('typeError');
      } else {
        const { itemList, ...options } = response;
        setItemMaster(itemList);
        setItemOptions(options);
      }
    } catch (error) {
      if (error instanceof Error && error.message !== 'typeError') {
        showApiError(error, API_CODE.ITEM_MASTER);
      }
      throw new Error();
    }
  };

  return (
    <Box sx={{ mt: `calc(${HEADER_HEIGHT} + 1rem)` }}>
      <Box sx={{ mx: '2rem' }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: '1rem' }}>
          {isAdmin(userData) &&
            (displayMode === MANAGE_ITEM_MODE.VIEW ? (
              <Button
                variant="outlined"
                color="primary"
                sx={manageItemStyle.button}
                disabled={
                  !(
                    categoryMaster.length > 0 &&
                    typeMaster.length > 0 &&
                    itemMaster.length > 0
                  )
                }
                onClick={onClickSort}
              >
                {t('BTN_SORT_MODE')}
              </Button>
            ) : displayMode === MANAGE_ITEM_MODE.SORT ? (
              <React.Fragment>
                <Button
                  variant="outlined"
                  color="primary"
                  sx={[manageItemStyle.button, { mr: '1rem' }]}
                  onClick={handleSortCancel}
                >
                  {t('BTN_CANCEL')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  sx={manageItemStyle.button}
                  onClick={onClickSortSave}
                >
                  {t('BTN_SAVE')}
                </Button>
              </React.Fragment>
            ) : (
              <></>
            ))}
          <Box sx={{ ml: '1rem' }}>
            <Button
              variant="contained"
              onClick={() => doExportInspectionItemMaster()}
              fullWidth
              sx={[manageItemStyle.button, { backgroundColor: '#E0356C' }]}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FileUploadOutlined sx={{ fontSize: '1.3rem' }} />
                {t('BTN_EXPORT')}
              </Box>
            </Button>
          </Box>
          {/* 開発者向けインポートボタン */}
          <Button
            variant="contained"
            onClick={() => setIsOpenImportModal(true)}
            fullWidth
            sx={[
              manageItemStyle.button,
              {
                backgroundColor: '#1E83BE',
                width: '150px',
                ml: '1rem',
                display: 'none',
              },
            ]}
          >
            IMPORT
          </Button>
        </Box>
        <InspectionItemTable
          categoryMaster={categoryMaster}
          setCategoryMaster={setCategoryMaster}
          typeMaster={typeMaster}
          setTypeMaster={setTypeMaster}
          itemMaster={itemMaster}
          setItemMaster={setItemMaster}
          options={itemOptions}
          displayMode={displayMode}
          setDisplayMode={setDisplayMode}
          isTableDisplay={
            categoryMaster.length > 0 &&
            typeMaster.length > 0 &&
            itemMaster.length > 0
          }
        />
      </Box>
      <FileImportModal
        isOpen={isOpenImportModal}
        handleClose={onClickImportCancel}
        onClickOk={onClickImportOk}
        acceptableFiles={IMPORTABLE_EXCEL_FORMAT_LIST}
        setFile={setImportFile}
        file={importFile}
        status={importStatus}
        errorMsg={importErrorMsg}
      />
      {error && <ErrorModal message={error} onClose={hideError} />}
      <Backdrop
        sx={{
          ...LOADING_BACKDROP_STYLE,
          zIndex: (theme) => theme.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Box>
  );
};

export default ManageInspectionItem;
